// Exact shape of every log record the logger emits. Transports consume this
// type directly — read `record.context?.serviceName` (etc.) when they need a
// specific enrichment field, or iterate all keys when forwarding.
//
// `record.context` is the merged view that the dispatcher builds on every
// call:
//
//   { ...systemEnrichment, ...callerContext }   // caller wins on collision
//
// then masked as a single pass before transports run.

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

// Numeric priority for level comparisons. Higher = more severe. `silent`
// (configured via env) bypasses all transports entirely and is handled at
// the Logger boundary, not via this map.
export const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
}

// Origin frame for an error — parsed from `Error.stack` by stackTrace.ts.
// Any field may be undefined on engines whose stack format is unfamiliar
// (older Safari, some test runners). Never throws.
export interface OriginFrame {
  /** Calling function name, when the engine includes it. */
  function?: string
  /** Source file URL/path. */
  file?: string
  /** 1-indexed line number. */
  line?: number
}

// Context that ships with every log record. The first block is the system
// enrichment the dispatcher injects automatically; the index signature
// captures arbitrary caller-supplied keys.
//
// Caller-supplied keys win on collision (the dispatcher merges
// `{ ...enrichment, ...callerContext }`) so feature code can intentionally
// override e.g. `userId` for impersonation-style debugging.
export interface LogContext {
  // ── System enrichment ──────────────────────────────────────────────────
  /** Build-time app name (`config.app.name`). Present on every record. */
  serviceName?: string
  /** Build-time app version (`config.app.version`). Present on every record. */
  serviceVersion?: string
  /** Stable for the whole browser session. Present on every record. */
  sessionId?: string
  /** Set after `identifyUser()`. Absent while unauthenticated. */
  userId?: string
  /** Error-level only, when `error` is an Error with a parseable stack. */
  origin?: OriginFrame

  // ── Caller-supplied ────────────────────────────────────────────────────
  [key: string]: unknown
}

export interface LogRecord {
  /** ISO 8601 timestamp at the moment the record was constructed. */
  timestamp: string
  level: LogLevel
  /** Hierarchical scope from `logger.child('a').child('b')` → `'a:b'`. */
  scope?: string
  message: string
  /**
   * Merged system enrichment + caller context, already masked.
   * See `LogContext` for the documented enrichment fields.
   */
  context?: LogContext
  /** Raw error value passed to `logger.error(msg, err, ctx)`. */
  error?: unknown
}

export interface Transport {
  name: string
  // `record.context` is already masked by the logger before reaching here.
  write(record: LogRecord): void
}
